import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, Button, Collapse, Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { StatusPill } from './StatusPill';
import { MediaGallery, MediaLightbox } from './MediaGallery';
import { ActionDialog } from './ActionDialog';
import { AssignVendorModal } from './AssignVendorModal';
import {
  getPreMoveoutInspection,
  getPreMoveoutSignedUrls,
  deletePreMoveoutInspection,
  cancelPreMoveoutInspection,
  reopenPreMoveoutInspection,
  completePreMoveoutInspection,
  resendPreMoveoutAssignment,
  cancelPreMoveoutAssignment,
  approvePreMoveoutAssignment,
  declinePreMoveoutAssignment,
} from '../Helpers/Api';
import {
  fmtDate,
  fmtTime,
  fmtDateTime,
  fmtMoney,
  collectMediaKeys,
  fileNameFromKey,
  ASSIGNMENT_KIND,
  CLEANLINESS_RATINGS,
  HISTORY_ICONS,
  HISTORY_LABELS,
  propertyLabel,
  personName,
} from '../Helpers/format';

const RATING_TONE = { clean: 'approved', okay: 'quote_received', dirty: 'cancelled' };
const ACTIVE_FOR_ASSIGN = ['submitted', 'vendor_assigned', 'quote_received', 'approved'];

/**
 * Read-mostly detail view with report and assignment actions.
 *
 * Props
 * - isOpen, onClose(), reportId
 * - refreshToken: bump to force a reload (e.g. after the edit form saved)
 * - onChanged(doc?) — after any mutation (parent refreshes its list)
 * - onEdit(doc) — open the edit form
 * - onDeleted() — after a successful delete (parent closes + refreshes)
 * - readOnly — hides every mutating control (auditor)
 * - zIndex
 */
const PreMoveoutDetailModal = ({
  isOpen,
  onClose,
  reportId,
  refreshToken = 0,
  onChanged,
  onEdit,
  onDeleted,
  readOnly = false,
  zIndex = 1050,
}) => {
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signedUrls, setSignedUrls] = useState({});
  const [lightbox, setLightbox] = useState({ open: false, items: [], index: 0 });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // { type, assignment? }
  const [assignOpen, setAssignOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const fetchedKeysRef = useRef(new Set());

  /* ------------------------------------------------ load */
  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const { data, meta: m } = await getPreMoveoutInspection(reportId);
      setDoc(data);
      setMeta(m || {});
      const keys = collectMediaKeys(data).filter((k) => !fetchedKeysRef.current.has(k));
      if (keys.length) {
        keys.forEach((k) => fetchedKeysRef.current.add(k));
        const urls = await getPreMoveoutSignedUrls(keys);
        setSignedUrls((prev) => ({ ...prev, ...(urls || {}) }));
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load the inspection');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    if (isOpen) {
      load();
    } else {
      setDoc(null);
      setMeta({});
      setSignedUrls({});
      fetchedKeysRef.current = new Set();
      setHistoryOpen(false);
      setDialog(null);
      setAssignOpen(false);
    }
  }, [isOpen, load, refreshToken]);

  /* ------------------------------------------------ mutation runner */
  const run = async (fn, successMessage) => {
    if (busy) return false;
    setBusy(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      await load();
      if (onChanged) onChanged();
      return true;
    } catch (err) {
      toast.error(err.message || 'The action failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (title, text, confirmText = 'Confirm', danger = false) => {
    const r = await Swal.fire({
      title,
      text,
      icon: danger ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: confirmText,
      confirmButtonColor: danger ? '#fd625e' : '#0e578e',
    });
    return r.isConfirmed;
  };

  /* ------------------------------------------------ report actions */
  const handleDelete = async () => {
    if (!doc) return;
    const ok = await confirm('Delete this draft?', `Unit ${doc.unit_number} — this cannot be undone.`, 'Delete', true);
    if (!ok) return;
    setBusy(true);
    try {
      await deletePreMoveoutInspection(doc._id);
      toast.success('Inspection deleted');
      if (onDeleted) onDeleted();
      else {
        if (onChanged) onChanged();
        onClose();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!doc) return;
    const ok = await confirm('Reopen this inspection?', 'It goes back to its previous working state.', 'Reopen');
    if (!ok) return;
    run(() => reopenPreMoveoutInspection(doc._id), 'Inspection reopened');
  };

  const handleResend = async (a) => {
    const ok = await confirm('Resend the quote request?', `A fresh email goes to ${a.vendor_email}.`, 'Resend');
    if (!ok) return;
    run(() => resendPreMoveoutAssignment(doc._id, a._id), 'Quote request resent');
  };

  /* dialog-driven actions (reason / amount inputs) */
  const dialogConfig = useMemo(() => {
    if (!dialog || !doc) return null;
    const a = dialog.assignment;
    switch (dialog.type) {
      case 'approve':
        return {
          title: 'Approve quote',
          description: `${a.vendor_name || a.vendor_email} quoted ${fmtMoney(a.quote?.amount)}. Adjust the approved amount if it differs.`,
          confirmLabel: 'Approve',
          confirmColor: 'success',
          fields: [
            { name: 'amount', label: 'Approved amount (CAD)', type: 'number', prefix: '$', defaultValue: a.quote?.amount ?? '' },
            { name: 'note', label: 'Note to vendor', type: 'textarea', placeholder: 'Optional' },
          ],
          onConfirm: async (v) => {
            const ok = await run(
              () => approvePreMoveoutAssignment(doc._id, a._id, { amount: v.amount, note: v.note }),
              'Quote approved'
            );
            if (ok) setDialog(null);
          },
        };
      case 'decline':
        return {
          title: 'Decline quote',
          description: `Decline the ${fmtMoney(a.quote?.amount)} quote from ${a.vendor_name || a.vendor_email}.`,
          confirmLabel: 'Decline',
          confirmColor: 'danger',
          fields: [{ name: 'reason', label: 'Reason', type: 'textarea', required: true, placeholder: 'Shared with the vendor' }],
          onConfirm: async (v) => {
            const ok = await run(() => declinePreMoveoutAssignment(doc._id, a._id, v.reason.trim()), 'Quote declined');
            if (ok) setDialog(null);
          },
        };
      case 'cancel_assignment':
        return {
          title: 'Cancel assignment',
          description: `${a.vendor_name || a.vendor_email} will no longer be able to quote on this job.`,
          confirmLabel: 'Cancel assignment',
          confirmColor: 'danger',
          fields: [{ name: 'reason', label: 'Reason', type: 'textarea', required: true }],
          onConfirm: async (v) => {
            const ok = await run(() => cancelPreMoveoutAssignment(doc._id, a._id, v.reason.trim()), 'Assignment cancelled');
            if (ok) setDialog(null);
          },
        };
      case 'cancel_report':
        return {
          title: 'Cancel inspection',
          description: 'Open vendor assignments stay on record; the report can be reopened later.',
          confirmLabel: 'Cancel inspection',
          confirmColor: 'danger',
          fields: [{ name: 'reason', label: 'Reason', type: 'textarea', required: true }],
          onConfirm: async (v) => {
            const ok = await run(() => cancelPreMoveoutInspection(doc._id, v.reason.trim()), 'Inspection cancelled');
            if (ok) setDialog(null);
          },
        };
      case 'complete':
        return {
          title: 'Mark completed',
          description: 'Use this once the work on the unit is done.',
          confirmLabel: 'Mark completed',
          confirmColor: 'success',
          fields: [{ name: 'note', label: 'Note', type: 'textarea', placeholder: 'Optional' }],
          onConfirm: async (v) => {
            const ok = await run(() => completePreMoveoutInspection(doc._id, v.note.trim()), 'Inspection completed');
            if (ok) setDialog(null);
          },
        };
      default:
        return null;
    }
  }, [dialog, doc]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------------------------ derived flags */
  const status = doc?.status;
  const isTerminal = status === 'cancelled' || status === 'completed';
  const locked = (doc?.assignments || []).some(a => a.approval?.at);
  const canEdit = !!doc && !isTerminal && !readOnly && !locked;
  const canAssign = !!doc && ACTIVE_FOR_ASSIGN.includes(status) && !readOnly && !locked;
  const canComplete = !!doc && !isTerminal && !readOnly && !locked;
  const canCancel = !!doc && !isTerminal && !readOnly && !locked;
  const canReopen = !!doc && isTerminal && !readOnly && !locked;
  const canDelete = !!doc && status === 'draft' && !readOnly;
  const canApprove = !!meta.canApprove && !readOnly;

  const openLightbox = (keys, index) =>
    setLightbox({
      open: true,
      index,
      items: keys.map((k) => ({ key: k, url: signedUrls[k] || null, name: fileNameFromKey(k) })),
    });

  const history = useMemo(
    () => [...(doc?.history || [])].sort((a, b) => new Date(b.at) - new Date(a.at)),
    [doc]
  );

  const rating = CLEANLINESS_RATINGS.find((r) => r.value === doc?.cleanliness?.rating);

  /* ------------------------------------------------ render */
  const renderAssignment = (a) => {
    const kindLabel = ASSIGNMENT_KIND[a.kind] || a.kind;
    const live = !['declined', 'cancelled'].includes(a.status);
    return (
      <div className="pm-assignment-card" key={a._id}>
        <div className="pm-assignment-card__head">
          <div className="d-flex align-items-start gap-2">
            <span className={`pm-kind pm-kind--${a.kind}`}>{kindLabel}</span>
            <div className="pm-assignment-card__vendor">
              {a.vendor_name || a.vendor_email}
              {a.vendor_name && <small>{a.vendor_email}</small>}
            </div>
          </div>
          <StatusPill status={a.status} kind="assignment" />
        </div>

        {a.appointment_date && <p className="pm-workflow-note"><FeatherIcon icon="calendar" size={14} className="me-2" />{a.appointment_confirmed_at ? 'Appointment confirmed' : 'Proposed appointment'}: {fmtDate(a.appointment_date)} at {a.appointment_time} (property local time)</p>}
        {a.scope && <p className="pm-assignment-card__scope">{a.scope}</p>}

        <div className="pm-assignment-card__grid">
          <div>
            <div className="lbl">Assigned</div>
            <div className="val">
              {fmtDateTime(a.assigned_at)}
              {personName(a.assigned_by) ? ` · ${personName(a.assigned_by)}` : ''}
            </div>
          </div>
          <div>
            <div className="lbl">Internal estimate</div>
            <div className="val">{fmtMoney(a.estimate_price)}</div>
          </div>
          {a.last_emailed_at && (
            <div>
              <div className="lbl">Last emailed</div>
              <div className="val">{fmtDateTime(a.last_emailed_at)}</div>
            </div>
          )}
        </div>

        {a.quote && (
          <div className="pm-assignment-card__quote">
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
              <span>
                Quote <span className="amt">{fmtMoney(a.quote.amount)}</span>
              </span>
              <span className="text-muted">
                {fmtDateTime(a.quote.submitted_at)}
                {a.quote.submitted_by_email ? ` · ${a.quote.submitted_by_email}` : ''}
              </span>
            </div>
            {a.quote.notes && <p className="pm-note mt-1 mb-0">{a.quote.notes}</p>}
          </div>
        )}

        {a.approval && (
          <div className="pm-assignment-card__quote">
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
              <span>
                Approved <span className="amt">{fmtMoney(a.approval.amount)}</span>
              </span>
              <span className="text-muted">
                {fmtDateTime(a.approval.at)}
                {a.approval.by_name || a.approval.by_email ? ` · ${a.approval.by_name || a.approval.by_email}` : ''}
              </span>
            </div>
            {a.approval.note && <p className="pm-note mt-1 mb-0">{a.approval.note}</p>}
          </div>
        )}

        {a.declined && (
          <div className="pm-assignment-card__quote">
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
              <span className="fw-semibold">Rejected</span>
              <span className="text-muted">
                {fmtDateTime(a.declined.at)}
                {a.declined.by_name || a.declined.by_email ? ` · ${a.declined.by_name || a.declined.by_email}` : ''}
              </span>
            </div>
            {a.declined.reason && <p className="pm-note mt-1 mb-0">{a.declined.reason}</p>}
          </div>
        )}

        {a.cancelled && (
          <div className="pm-assignment-card__quote">
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
              <span className="fw-semibold">Cancelled</span>
              <span className="text-muted">
                {fmtDateTime(a.cancelled.at)}
                {a.cancelled.by_name ? ` · ${a.cancelled.by_name}` : ''}
              </span>
            </div>
            {a.cancelled.reason && <p className="pm-note mt-1 mb-0">{a.cancelled.reason}</p>}
          </div>
        )}

        {!readOnly && !isTerminal && live && a.status !== 'approved' && (
          <div className="pm-assignment-card__actions">
            {!locked && <Button color="light" size="sm" disabled={busy} onClick={() => { setEditingAssignment(a); setAssignOpen(true); }}>Edit request / date</Button>}
            {a.status === 'assigned' && (
              <Button color="light" size="sm" onClick={() => handleResend(a)} disabled={busy}>
                <FeatherIcon icon="repeat" size={14} className="me-1" />
                Resend request
              </Button>
            )}
            {a.status === 'quoted' && canApprove && (
              <>
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setDialog({ type: 'approve', assignment: a })}
                  disabled={busy}
                >
                  <FeatherIcon icon="check" size={14} className="me-1" />
                  Approve
                </Button>
                <Button
                  color="danger"
                  outline
                  size="sm"
                  onClick={() => setDialog({ type: 'decline', assignment: a })}
                  disabled={busy}
                >
                  <FeatherIcon icon="x" size={14} className="me-1" />
                  Reject
                </Button>
              </>
            )}
            {a.status === 'quoted' && !canApprove && (
              <span className="text-muted small align-self-center">Awaiting an approver's decision</span>
            )}
            {!locked && (a.status === 'assigned' || a.status === 'quoted') && (
              <Button
                color="light"
                size="sm"
                onClick={() => setDialog({ type: 'cancel_assignment', assignment: a })}
                disabled={busy}
              >
                <FeatherIcon icon="user-x" size={14} className="me-1" />
                Cancel assignment
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  const cleanKeys = doc?.cleanliness?.attachments || [];

  return (
    <>
      <Modal isOpen={isOpen} toggle={busy ? undefined : onClose} size="xl" scrollable zIndex={zIndex} labelledBy="pm-detail-title">
        <div className="pm-modal-header">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div>
              <h5 id="pm-detail-title">
                {doc ? `Unit ${doc.unit_number || '—'}` : 'Pre-moveout inspection'}
                {doc?.property_details?.building_name ? ` · ${doc.property_details.building_name}` : ''}
              </h5>
              <div className="pm-modal-sub">
                {doc ? (
                  <>
                    {propertyLabel(doc) || 'Property'}
                    {' · Move-out '}
                    {fmtDate(doc.moveout_date)}
                    {fmtTime(doc.moveout_time) ? ` at ${fmtTime(doc.moveout_time)}` : ''}
                  </>
                ) : (
                  'Loading…'
                )}
              </div>
            </div>
            {doc && <StatusPill status={doc.status} />}
          </div>
          <button type="button" className="pm-icon-btn" onClick={onClose} aria-label="Close" disabled={busy}>
            <FeatherIcon icon="x" size={18} />
          </button>
        </div>

        <ModalBody className="pm-detail">
          {loading && !doc ? (
            <div className="pm-empty" role="status" aria-live="polite">
              <Spinner size="sm" color="primary" className="me-2" />
              Loading inspection…
            </div>
          ) : !doc ? (
            <div className="pm-empty">
              <FeatherIcon icon="alert-circle" size={28} />
              <h6>Could not load this inspection</h6>
              <p>Close and try again.</p>
            </div>
          ) : (
            <>
              {(doc.cancelled || doc.completed) && (
                <div className="pm-blocker mb-3 mt-0" role="status">
                  <FeatherIcon icon={doc.cancelled ? 'slash' : 'check-square'} size={16} />
                  <span>
                    {doc.status === 'cancelled' && doc.cancelled ? (
                      <>
                        Cancelled {fmtDateTime(doc.cancelled.at)}
                        {personName(doc.cancelled.by) ? ` by ${personName(doc.cancelled.by)}` : ''}
                        {doc.cancelled.reason ? ` — ${doc.cancelled.reason}` : ''}
                      </>
                    ) : doc.status === 'completed' && doc.completed ? (
                      <>
                        Completed {fmtDateTime(doc.completed.at)}
                        {personName(doc.completed.by) ? ` by ${personName(doc.completed.by)}` : ''}
                      </>
                    ) : (
                      'This inspection was reopened.'
                    )}
                  </span>
                </div>
              )}

              <div className="pm-section">
                <div className="pm-section__head">
                  <h6>Overview</h6>
                </div>
                <dl className="pm-kv">
                  <dt>Property</dt>
                  <dd>
                    {propertyLabel(doc) || '—'}
                    {doc.property_details?.building_name ? ` · ${doc.property_details.building_name}` : ''}
                  </dd>
                  <dt>Unit</dt>
                  <dd>{doc.unit_number || '—'}</dd>
                  <dt>Pre-inspection</dt>
                  <dd>{fmtDate(doc.pre_inspection_date)}</dd>
                  <dt>Move-out</dt>
                  <dd>
                    {fmtDate(doc.moveout_date)}
                    {fmtTime(doc.moveout_time) ? ` at ${fmtTime(doc.moveout_time)}` : ''}
                  </dd>
                  <dt>Inspected by</dt>
                  <dd>{personName(doc.inspected_by) || '—'}</dd>
                  <dt>Submitted</dt>
                  <dd>{doc.submitted_at ? fmtDateTime(doc.submitted_at) : 'Not yet'}</dd>
                  <dt>Updated</dt>
                  <dd>{fmtDateTime(doc.updatedAt)}</dd>
                </dl>
              </div>

              <div className="pm-section">
                <div className="pm-section__head">
                  <h6>Cleanliness</h6>
                  {rating ? (
                    <span className={`pm-pill pm-pill--${RATING_TONE[rating.value]}`}>
                      <FeatherIcon icon={rating.icon} size={13} />
                      {rating.label}
                    </span>
                  ) : (
                    <span className="text-muted small">Not rated</span>
                  )}
                </div>
                {doc.cleanliness?.notes ? (
                  <p className="pm-note mb-2">{doc.cleanliness.notes}</p>
                ) : (
                  <p className="pm-note text-muted mb-2">No notes.</p>
                )}
                <MediaGallery keys={cleanKeys} signedUrls={signedUrls} onOpen={(i) => openLightbox(cleanKeys, i)} />
              </div>

              <div className="pm-section">
                <div className="pm-section__head">
                  <h6>Repairs ({(doc.repairs || []).length})</h6>
                </div>
                {(doc.repairs || []).length === 0 ? (
                  <p className="pm-note text-muted">No repairs recorded.</p>
                ) : (
                  (doc.repairs || []).map((r, i) => {
                    const keys = r.attachments || [];
                    return (
                      <div className="pm-repair-item" key={r._id || i}>
                        <div className="pm-repair-item__title">
                          {i + 1}. {r.category || 'Uncategorised'}
                        </div>
                        {r.description && <p className="pm-note mb-1">{r.description}</p>}
                        <MediaGallery keys={keys} signedUrls={signedUrls} onOpen={(idx) => openLightbox(keys, idx)} />
                      </div>
                    );
                  })
                )}
              </div>

              <div className="pm-section">
                <div className="pm-section__head">
                  <h6>Damages &amp; comments</h6>
                </div>
                <dl className="pm-kv">
                  <dt>Damages</dt>
                  <dd>
                    <span className="pm-note">{doc.damages_notes || '—'}</span>
                  </dd>
                  <dt>Comments</dt>
                  <dd>
                    <span className="pm-note">{doc.comments || '—'}</span>
                  </dd>
                </dl>
              </div>

              <div className="pm-section">
                <div className="pm-section__head">
                  <h6>Vendors &amp; quotes ({(doc.assignments || []).length})</h6>
                  {canAssign && (
                    <Button color="primary" size="sm" onClick={() => { setEditingAssignment(null); setAssignOpen(true); }} disabled={busy} style={{ minHeight: 38 }}>
                      <FeatherIcon icon="user-plus" size={14} className="me-1" />
                      Assign vendor
                    </Button>
                  )}
                </div>
                {(doc.assignments || []).length === 0 ? (
                  <p className="pm-note text-muted">
                    {doc.status === 'draft'
                      ? 'Submit the inspection before assigning a vendor.'
                      : 'No vendors assigned yet.'}
                  </p>
                ) : (
                  (doc.assignments || []).map(renderAssignment)
                )}
              </div>

              <div className="pm-section">
                <div className="pm-section__head">
                  <h6>History ({history.length})</h6>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => setHistoryOpen((o) => !o)}
                    aria-expanded={historyOpen}
                    aria-controls="pm-history-list"
                  >
                    {historyOpen ? 'Hide' : 'Show'}
                    <FeatherIcon icon={historyOpen ? 'chevron-up' : 'chevron-down'} size={14} className="ms-1" />
                  </Button>
                </div>
                <Collapse isOpen={historyOpen}>
                  <ul className="pm-history" id="pm-history-list">
                    {history.length === 0 && <li className="text-muted">No history yet.</li>}
                    {history.map((h) => (
                      <li key={h._id || `${h.at}-${h.action}`}>
                        <span className="pm-history__icon" aria-hidden="true">
                          <FeatherIcon icon={HISTORY_ICONS[h.action] || 'activity'} size={14} />
                        </span>
                        <div className="pm-history__body">
                          <strong>{HISTORY_LABELS[h.action] || h.action}</strong>
                          <div className="pm-history__meta">
                            {personName(h.by) ? `by ${personName(h.by)} · ` : ''}
                            {fmtDateTime(h.at)}
                          </div>
                          {h.note && <div className="pm-history__note">{h.note}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </div>
            </>
          )}
        </ModalBody>

        <div className="pm-modal-footer">
          <div className="d-flex gap-2 flex-wrap">
            {canDelete && (
              <Button color="danger" outline onClick={handleDelete} disabled={busy}>
                <FeatherIcon icon="trash-2" size={15} className="me-1" />
                Delete draft
              </Button>
            )}
            {canCancel && status !== 'draft' && (
              <Button color="danger" outline onClick={() => setDialog({ type: 'cancel_report' })} disabled={busy}>
                <FeatherIcon icon="slash" size={15} className="me-1" />
                Cancel inspection
              </Button>
            )}
            {canReopen && (
              <Button color="light" onClick={handleReopen} disabled={busy}>
                <FeatherIcon icon="rotate-ccw" size={15} className="me-1" />
                Reopen
              </Button>
            )}
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button color="light" onClick={onClose} disabled={busy}>
              Close
            </Button>
            {canComplete && (
              <Button color="success" outline onClick={() => setDialog({ type: 'complete' })} disabled={busy}>
                <FeatherIcon icon="check-square" size={15} className="me-1" />
                Mark completed
              </Button>
            )}
            {canEdit && (
              <Button color={status === 'draft' ? 'primary' : 'light'} onClick={() => onEdit && onEdit(doc)} disabled={busy}>
                <FeatherIcon icon="edit-2" size={15} className="me-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <MediaLightbox
        isOpen={lightbox.open}
        items={lightbox.items}
        index={lightbox.index}
        onIndexChange={(i) => setLightbox((l) => ({ ...l, index: i }))}
        onClose={() => setLightbox({ open: false, items: [], index: 0 })}
      />

      {dialogConfig && (
        <ActionDialog
          isOpen={!!dialog}
          title={dialogConfig.title}
          description={dialogConfig.description}
          fields={dialogConfig.fields}
          confirmLabel={dialogConfig.confirmLabel}
          confirmColor={dialogConfig.confirmColor}
          onConfirm={dialogConfig.onConfirm}
          onClose={() => setDialog(null)}
          zIndex={zIndex + 20}
        />
      )}

      <AssignVendorModal
        isOpen={assignOpen}
        assignment={editingAssignment}
        onClose={() => setAssignOpen(false)}
        report={doc}
        zIndex={zIndex + 15}
        onAssigned={async () => {
          await load();
          if (onChanged) onChanged();
        }}
      />
    </>
  );
};

export { PreMoveoutDetailModal };
export default PreMoveoutDetailModal;
